import { useRef, createContext } from 'react';
import { Loader2 } from 'lucide-react';
import StepperBar from './StepperBar';
import NavigationButtons from './NavigationButtons';
import CameraDebugHUD from '../CameraDebugHUD';
import useNavPin from './useNavPin';

// Context lets nested step components (e.g. ProductTypeStep) render the step's
// navigation buttons INSIDE their own card, instead of CustomizerLayout rendering
// them separately below.
export const StepNavigationContext = createContext(null);

const CustomizerLayout = ({
  cushion,
  loading,
  steps,
  currentStepIndex,
  currentStepName,
  onStepClick,
  isFirstStep,
  isReviewStep,
  canProceed,
  onBack,
  onNext,
  onAddToCart,
  addingToCart,
  priceInfo,
  pipingCost = 0,
  previewPanel,
  contentPanel,
  pageTestId,
}) => {
  const sentinelRef = useRef(null);
  const buttonsRef = useRef(null);
  const spacerRef = useRef(null);

  useNavPin(sentinelRef, buttonsRef, spacerRef, [currentStepIndex]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading">
        <Loader2 className="h-8 w-8 animate-spin text-bush" />
      </div>
    );
  }

  if (!cushion) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="not-found">
        <p className="text-gray-500">Product not found.</p>
      </div>
    );
  }

  const stepsWithClick = steps.map((step, i) => ({
    ...step,
    onClickBack: i < currentStepIndex ? () => onStepClick(i) : undefined,
  }));

  const navigationEl = (
    <NavigationButtons
      isFirstStep={isFirstStep}
      isReviewStep={isReviewStep}
      canProceed={canProceed}
      onBack={onBack}
      onNext={onNext}
      onAddToCart={onAddToCart}
      addingToCart={addingToCart}
      priceInfo={priceInfo}
      pipingCost={pipingCost}
    />
  );

  const isProductStep = currentStepName === 'Product';

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-sand overflow-x-hidden" data-testid={pageTestId}>
      <CameraDebugHUD />

      {/* Product title bar — sits flush with the StepperBar below (no gap). */}
      <div className="bg-white border-b border-[#f7f7f7] pt-4 sm:pt-6 pb-3 sm:pb-4 mt-0 sm:mt-6" data-testid="product-title-bar">
        <div className="max-w-[1632px] mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-lg sm:text-xl font-display font-semibold text-[#1A1A1A] tracking-tight">
            {cushion.name}
          </h2>
        </div>
      </div>

      <StepperBar steps={stepsWithClick} currentStepIndex={currentStepIndex} />

      <div id="CustomizerLayout_150" className={`w-full max-w-[1632px] mx-auto px-4 sm:px-6 lg:px-12 ${isProductStep ? 'pt-6 lg:pt-6 pb-6 lg:pb-12' : 'pt-6 lg:pt-6 pb-6 lg:pb-12'} box-border overflow-x-hidden`}>
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {!isProductStep && previewPanel}

          <div className={`${isProductStep ? 'order-2 lg:order-2 overflow-x-hidden lg:col-span-2' : 'bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-card order-2 lg:order-2 overflow-x-hidden'}`}>
            <StepNavigationContext.Provider value={isProductStep ? navigationEl : null}>
              {contentPanel}
            </StepNavigationContext.Provider>

            {/* Non-Product steps: keep inline divider + global navigation buttons */}
            {!isProductStep && (
              <>
                <div className="border-b border-[#e3e0dd] mt-[15px] mb-[15px] lg:mt-[25px] lg:mb-[25px]" />

                {/* Sentinel: zero-height marker BEFORE buttons so its position is stable */}
                <div ref={sentinelRef} style={{ height: 0, overflow: 'hidden' }} />

                <div ref={buttonsRef}>
                  {navigationEl}
                </div>
                {/* Spacer to prevent layout jump when buttons go fixed */}
                <div ref={spacerRef} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizerLayout;
